import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Send, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    startupName: "",
    founderName: "",
    founderEmail: "",
    founderPhone: "",
    location: "",
    coFounderName: "",
    websiteUrl: "",
    founderLinkedIn: "",
    coFounderLinkedIn: "",
    ideaDescription: "",
    currentStage: "",
    teamSize: "",
    traction: "",
    whyWayanad: "",
    expectedOutcome: "",
    expectationFromProgram: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    const requiredFields = ['startupName', 'founderName', 'founderEmail', 'founderPhone', 'location', 'ideaDescription', 'currentStage'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/applications`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(response.data.message);
      } else {
        toast.error('Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Application submission error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit application. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="apply" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in Modern Innovation Hub. Our team will review your application and get back to you within 5-7 business days.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              >
                Submit Another Application
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4">
            Apply Now
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Join Our Next Cohort
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Take the first step towards transforming your startup idea into a successful business. 
            Fill out the application below and our team will review your submission.
          </p>
        </div>

        <Card className="border-0 shadow-xl bg-white">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl text-gray-900">Startup Application Form</CardTitle>
            <p className="text-gray-600">Please provide detailed information about your startup and vision.</p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="startupName">Startup Name *</Label>
                  <Input
                    id="startupName"
                    value={formData.startupName}
                    onChange={(e) => handleInputChange('startupName', e.target.value)}
                    placeholder="Enter your startup name"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="websiteUrl">Website URL</Label>
                  <Input
                    id="websiteUrl"
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>

              {/* Founder Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="founderName">Founder Name *</Label>
                  <Input
                    id="founderName"
                    value={formData.founderName}
                    onChange={(e) => handleInputChange('founderName', e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="coFounderName">Co-Founder Name</Label>
                  <Input
                    id="coFounderName"
                    value={formData.coFounderName}
                    onChange={(e) => handleInputChange('coFounderName', e.target.value)}
                    placeholder="Co-founder's name (if any)"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="founderEmail">Email Address *</Label>
                  <Input
                    id="founderEmail"
                    type="email"
                    value={formData.founderEmail}
                    onChange={(e) => handleInputChange('founderEmail', e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="founderPhone">Phone Number *</Label>
                  <Input
                    id="founderPhone"
                    type="tel"
                    value={formData.founderPhone}
                    onChange={(e) => handleInputChange('founderPhone', e.target.value)}
                    placeholder="+91 9876543210"
                    required
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="City, State, Country"
                  required
                />
              </div>

              {/* LinkedIn Profiles */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="founderLinkedIn">Founder LinkedIn Profile</Label>
                  <Input
                    id="founderLinkedIn"
                    type="url"
                    value={formData.founderLinkedIn}
                    onChange={(e) => handleInputChange('founderLinkedIn', e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="coFounderLinkedIn">Co-Founder LinkedIn Profile</Label>
                  <Input
                    id="coFounderLinkedIn"
                    type="url"
                    value={formData.coFounderLinkedIn}
                    onChange={(e) => handleInputChange('coFounderLinkedIn', e.target.value)}
                    placeholder="https://linkedin.com/in/cofounderprofile"
                  />
                </div>
              </div>

              {/* Startup Details */}
              <div className="space-y-2">
                <Label htmlFor="ideaDescription">Startup Idea Description *</Label>
                <Textarea
                  id="ideaDescription"
                  value={formData.ideaDescription}
                  onChange={(e) => handleInputChange('ideaDescription', e.target.value)}
                  placeholder="Describe your startup idea, the problem you're solving, and your proposed solution..."
                  rows={4}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="currentStage">Current Stage *</Label>
                  <Select onValueChange={(value) => handleInputChange('currentStage', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select current stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idea">Idea Stage</SelectItem>
                      <SelectItem value="prototype">Prototype/MVP</SelectItem>
                      <SelectItem value="early-traction">Early Traction</SelectItem>
                      <SelectItem value="revenue">Generating Revenue</SelectItem>
                      <SelectItem value="scaling">Scaling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="teamSize">Team Size</Label>
                  <Select onValueChange={(value) => handleInputChange('teamSize', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Just me</SelectItem>
                      <SelectItem value="2-3">2-3 people</SelectItem>
                      <SelectItem value="4-6">4-6 people</SelectItem>
                      <SelectItem value="7-10">7-10 people</SelectItem>
                      <SelectItem value="10+">10+ people</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="traction">Current Traction</Label>
                <Textarea
                  id="traction"
                  value={formData.traction}
                  onChange={(e) => handleInputChange('traction', e.target.value)}
                  placeholder="Describe any traction you have - users, revenue, partnerships, pilot customers, etc."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="whyWayanad">Why are you interested in the Wayanad program?</Label>
                <Textarea
                  id="whyWayanad"
                  value={formData.whyWayanad}
                  onChange={(e) => handleInputChange('whyWayanad', e.target.value)}
                  placeholder="Tell us why you want to be part of Modern Innovation Hub and how it aligns with your goals..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedOutcome">Expected Outcome</Label>
                <Textarea
                  id="expectedOutcome"
                  value={formData.expectedOutcome}
                  onChange={(e) => handleInputChange('expectedOutcome', e.target.value)}
                  placeholder="What do you hope to achieve by the end of the 6-month program?"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectationFromProgram">What You Expect From Program</Label>
                <Textarea
                  id="expectationFromProgram"
                  value={formData.expectationFromProgram}
                  onChange={(e) => handleInputChange('expectationFromProgram', e.target.value)}
                  placeholder="What specific support, resources, or guidance do you expect from the Modern Innovation Hub program?"
                  rows={3}
                />
              </div>

              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 group"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      Submit Application
                      <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ApplicationForm;